import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, Plus, Pencil, Trash2, Loader2, Check, X, Linkedin, MailWarning } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export const AUTHORITY_OPTIONS = [
  { value: "decision_maker", label: "Decision Maker", desc: "Has the final authority to make decisions about purchasing, changing or cancelling EQUIPR services." },
  { value: "budget_holder", label: "Budget Holder", desc: "Controls or approves the budget and has authority over financial commitments." },
  { value: "influencer", label: "Influencer", desc: "Has significant input into decisions but does not have final approval." },
  { value: "system_champion", label: "System Champion", desc: "Supports EQUIPR internally, promotes its use and may influence other staff to adopt new features or processes." },
  { value: "primary_user", label: "Primary User", desc: "Uses EQUIPR regularly and understands the day-to-day requirements, but generally doesn't make purchasing decisions." },
  { value: "technical_contact", label: "Technical Contact", desc: "Responsible for IT, integrations, servers, security or technical requirements." },
  { value: "accounts_finance", label: "Accounts / Finance", desc: "Responsible for invoices, payments, accounting integrations and financial queries." },
  { value: "operations_contact", label: "Operations Contact", desc: "Responsible for the day-to-day operational use of EQUIPR, such as scheduling, dispatch, jobs or compliance." },
  { value: "owner_director", label: "Owner / Director", desc: "Business owner or senior executive with overall responsibility for the business." },
  { value: "other", label: "Other", desc: "Contact doesn't fit into one of the above categories." },
];
const AUTHORITY_LABEL = Object.fromEntries(AUTHORITY_OPTIONS.map((o) => [o.value, o.label]));

// Where an address came from decides whether we may use it, so it is on the
// contact rather than in somebody's head.
export const EMAIL_SOURCES = [
  { value: "company_published", label: "Published by the company", desc: "On their own site. The address anyone could find." },
  { value: "inbound_form", label: "They gave it to us", desc: "An enquiry or demo form, or they emailed us first." },
  { value: "enrichment", label: "From an enrichment tool", desc: "Bought or guessed. Held on the record, but outreach to it is blocked." },
];
const SOURCE_LABEL = Object.fromEntries(EMAIL_SOURCES.map((o) => [o.value, o.label]));

const empty = { first_name: "", last_name: "", email: "", phone_office: "", phone_mobile: "", authority: "", role: "", linkedin_url: "", email_source: "company_published" };

// base = e.g. `${API}/sales/prospects/{id}` or `${API}/customer-success/clients/{id}`
export function ContactsEditor({ base }) {
  const [contacts, setContacts] = useState([]);
  const [editing, setEditing] = useState(null); // id or "new"
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${base}/contacts`);
      setContacts(r.data.contacts || []);
    } catch (e) { /* ignore */ }
  }, [base]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setForm(empty); setEditing("new"); };
  const startEdit = (c) => { setForm({ first_name: c.first_name || "", last_name: c.last_name || "", email: c.email || "", phone_office: c.phone_office || c.phone || "", phone_mobile: c.phone_mobile || "", authority: c.authority || "", role: c.role || "", linkedin_url: c.linkedin_url || "", email_source: c.email_source || "company_published" }); setEditing(c.id); };

  const save = async () => {
    if (!form.first_name && !form.last_name && !form.email) { toast.error("Add at least a name or email"); return; }
    setSaving(true);
    try {
      if (editing === "new") await axios.post(`${base}/contacts`, form);
      else await axios.put(`${base}/contacts/${editing}`, form);
      toast.success("Contact saved");
      setEditing(null); await load();
    } catch (e) { toast.error("Could not save contact"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this contact?")) return;
    try { await axios.delete(`${base}/contacts/${id}`); await load(); }
    catch (e) { toast.error("Could not remove contact"); }
  };

  const fields = (
    <div className="grid grid-cols-2 gap-2 rounded-lg border border-[#FFF8D6] bg-[#FFF8D6] p-3">
      <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="First name" className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] h-8" data-testid="contact-first" />
      <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Last name" className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] h-8" data-testid="contact-last" />
      <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] h-8" data-testid="contact-email" />
      <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Role / Position" className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] h-8" data-testid="contact-role" />
      <Input value={form.phone_office} onChange={(e) => setForm({ ...form, phone_office: e.target.value })} placeholder="Office phone" className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] h-8" data-testid="contact-phone-office" />
      <Input value={form.phone_mobile} onChange={(e) => setForm({ ...form, phone_mobile: e.target.value })} placeholder="Mobile phone" className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] h-8" data-testid="contact-phone-mobile" />
      <Input value={form.linkedin_url} onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })} placeholder="LinkedIn profile (optional)" className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] h-8" data-testid="contact-linkedin" />
      <Select value={form.email_source || "company_published"} onValueChange={(v) => setForm({ ...form, email_source: v })}>
        <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] h-8" data-testid="contact-email-source"><SelectValue placeholder="Where the email came from" /></SelectTrigger>
        <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA] max-w-[420px]">
          {EMAIL_SOURCES.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              <span className="font-medium">{o.label}</span>
              <span className="block text-[11px] text-[#8A857A] whitespace-normal leading-snug">{o.desc}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="col-span-2">
        <Select value={form.authority || "none"} onValueChange={(v) => setForm({ ...form, authority: v === "none" ? "" : v })}>
          <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] h-8" data-testid="contact-authority"><SelectValue placeholder="Authority / role in decision" /></SelectTrigger>
          <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA] max-w-[420px]">
            <SelectItem value="none">— No authority set —</SelectItem>
            {AUTHORITY_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                <span className="font-medium">{o.label}</span>
                <span className="block text-[11px] text-[#8A857A] whitespace-normal leading-snug">{o.desc}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-2 flex justify-end gap-2">
        <Button size="sm" variant="ghost" className="h-7 text-[#59554D]" onClick={() => setEditing(null)}><X className="w-4 h-4" /></Button>
        <Button size="sm" className="h-7 bg-[#FFD108] hover:bg-[#FFF8D6] text-[#1A1A1A]" onClick={save} disabled={saving} data-testid="contact-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="rounded-lg border border-[#DAD5CA] bg-[#FFFFFF] p-4 space-y-2" data-testid="contacts-editor">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-[#8A857A] font-semibold flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5" /> Contacts ({contacts.length})
        </div>
        {editing !== "new" && (
          <Button size="sm" className="h-8 bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A] text-xs" onClick={startNew} data-testid="contact-add">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add contact
          </Button>
        )}
      </div>
      {editing === "new" && fields}
      {contacts.length === 0 && editing !== "new" && <p className="text-xs text-[#8A857A]">No contacts yet.</p>}
      {contacts.map((c) => (
        editing === c.id ? <div key={c.id}>{fields}</div> : (
          <div key={c.id} className="flex items-center justify-between gap-3 rounded-lg border border-[#DAD5CA] bg-[#FFFFFF] px-3 py-2" data-testid={`contact-row-${c.id}`}>
            <div className="min-w-0">
              <div className="text-sm text-[#1A1A1A] truncate flex items-center gap-2 flex-wrap">
                <span>{[c.first_name, c.last_name].filter(Boolean).join(" ") || "—"}{c.role ? <span className="text-[#8A857A]"> · {c.role}</span> : null}</span>
                {c.authority && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-[#FFF8D6] text-[#8A6D00]" data-testid={`contact-authority-${c.id}`}>{AUTHORITY_LABEL[c.authority] || c.authority}</span>}
                {c.email && c.email_source === "enrichment" && (
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded inline-flex items-center gap-1" style={{ background: "#FDEDE8", color: "#8E1212" }} title="Bought or guessed, so outreach to it is blocked. Find the address they publish, or ring them." data-testid={`contact-not-emailable-${c.id}`}>
                    <MailWarning className="w-3 h-3" /> Not emailable
                  </span>
                )}
                {c.linkedin_url && (
                  <a href={c.linkedin_url} target="_blank" rel="noreferrer" className="text-[10px] font-medium px-1.5 py-0.5 rounded inline-flex items-center gap-1 hover:underline" style={{ background: "#EEF4FF", color: "#2E5AAC" }} data-testid={`contact-linkedin-${c.id}`}>
                    <Linkedin className="w-3 h-3" /> LinkedIn
                  </a>
                )}
              </div>
              <div className="text-xs text-[#8A857A] truncate">
                {[c.email,
                  c.email && c.email_source && c.email_source !== "company_published"
                    && (SOURCE_LABEL[c.email_source] || c.email_source).toLowerCase(),
                  (c.phone_office || c.phone) && `Office ${c.phone_office || c.phone}`,
                  c.phone_mobile && `Mobile ${c.phone_mobile}`].filter(Boolean).join("  ·  ")}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button size="icon" variant="ghost" className="h-7 w-7 text-[#8A857A] hover:text-[#1A1A1A]" onClick={() => startEdit(c)} data-testid={`contact-edit-${c.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-[#8A857A] hover:text-[#C23A00]" onClick={() => remove(c.id)} data-testid={`contact-del-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        )
      ))}
    </div>
  );
}
